import useAccess from '@/hooks/useAccess';
import { useRouteMatch } from '@/hooks/useRouteMatch';
import ControlCenterDropIcon from '@/icons/amaruNewIcons/ControlCenterDropIcon';
import { I18n } from '@lingui/core';
import { t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import {
  Biotech,
  Business as BusinessIcon,
  Campaign,
  DesignServicesOutlined,
  DeviceHub as DeviceHubIcon,
  Folder as FolderIcon,
  Group as GroupIcon,
  Home as HomeIcon,
  Key as KeyIcon,
  Notifications,
  Payment,
  Rule as RuleIcon,
  ScatterPlot,
  ScheduleSend as ScheduleSendIcon,
  WifiProtectedSetupOutlined as WifiProtectedSetupOutlinedIcon,
} from '@mui/icons-material';
import ListIcon from '@mui/icons-material/List';
import { SvgIconProps } from '@mui/material';
import { FC, useMemo } from 'react';
import ControlCenterIcon from 'src/icons/amaruNewIcons/ControlCenterIcon';
import DGAReportIcon from 'src/icons/amaruNewIcons/DGAReportIcon';
import DeviceAnalysisIcon from 'src/icons/amaruNewIcons/DeviceAnalysisIcon';
import DistributionsIcon from 'src/icons/amaruNewIcons/DistributionIcon';
import GatesIcon from 'src/icons/amaruNewIcons/GatesIcon';
import IntegrationsIcon from 'src/icons/amaruNewIcons/IntegrationsIcon';
import ReportsIcon from 'src/icons/amaruNewIcons/ReportsIcon';

interface SidebarItemGroup {
  title: string;
  items: SidebarItem[];
}

interface SidebarItem {
  title: string;
  path: string;
  paramsPath?: string | string[];
  icon: FC<SvgIconProps>;
  permission: string;
  active?: boolean;
  requiresCaptaUser?: boolean;
}

const ALL_SIDEBAR_ITEM_GROUPS = (i18n: I18n): SidebarItemGroup[] => [
  {
    title: t(i18n)`Inicio`,
    items: [
      {
        title: t(i18n)`Inicio`,
        path: '/home-amaru',
        icon: HomeIcon,
        permission: 'Sec:Home',
      },
    ],
  },
  {
    title: t(i18n)`Monitoreo`,
    items: [
      {
        title: t(i18n)`Centro de Control`,
        path: '/control-center',
        icon: ControlCenterIcon,
        permission: 'Sec:CentroControl',
      },
      {
        title: t(i18n)`Dispositivos`,
        path: '/devices',
        icon: ListIcon,
        permission: 'Sec:Dispositivos',
      },
      {
        title: t(i18n)`Integraciones`,
        path: '/integrations',
        paramsPath: '/integrations/:deviceId',
        icon: IntegrationsIcon,
        permission: 'Sec:Integraciones',
      },
      {
        title: t(i18n)`Diagnóstico de Dispositivos`,
        path: '/support',
        paramsPath: '/support/:deviceId',
        icon: RuleIcon,
        permission: 'Sec:Soporte',
      },
      {
        title: t(i18n)`Alertas` + ' (Beta)',
        path: '/alerts',
        icon: ControlCenterDropIcon,
        permission: 'Sec:HistorialNotificacionesAlertas',
      },
    ],
  },
  {
    title: t(i18n)`Control`,
    items: [
      {
        title: t(i18n)`Compuertas`,
        path: '/gates',
        paramsPath: '/gates/administration/:gateId',
        icon: GatesIcon,
        permission: 'Sec:Compuertas',
      },
    ],
  },
  {
    title: t(i18n)`Análisis`,
    items: [
      {
        title: t(i18n)`Análisis de Estaciones`,
        path: '/devices-analysis',
        paramsPath: ['/devices-analysis/:deviceId'],
        icon: DeviceAnalysisIcon,
        permission: 'Sec:AnalisisEstaciones',
      },
      {
        title: t(i18n)`Análisis Avanzado de Estaciones` + ' (Alpha)',
        path: '/advanced-devices-analysis',
        paramsPath: ['/advanced-devices-analysis/:deviceId'],
        icon: ScatterPlot,
        permission: 'Sec:AnalisisAvanzado',
      },
      {
        title: t(i18n)`Sistema Reparte Maipo`,
        path: '/distribution-maipo',
        icon: DistributionsIcon,
        permission: 'Sec:CalculadoraMaipoMenu',
      },
      {
        title: t(i18n)`Reparto`,
        path: '/distribution-initialization',
        paramsPath: '/distribution-history',
        icon: DistributionsIcon,
        permission: 'Sec:Reparto',
      },
      {
        title: t(i18n)`Reportes`,
        path: '/reports',
        icon: ReportsIcon,
        permission: 'Sec:Reports',
      },
      {
        title: t(i18n)`Reportes DGA`,
        path: '/dga',
        icon: DGAReportIcon,
        permission: 'Sec:ReportesDGA',
      },
      {
        title: t(i18n)`Reportes SMA`,
        path: '/sma',
        icon: DGAReportIcon,
        permission: 'Sec:Sma',
      },
      {
        title: t(i18n)`Vista de Métricas`,
        path: '/metrics-views',
        icon: DeviceAnalysisIcon,
        permission: 'Sec:MetricsViews',
        requiresCaptaUser: true,
      },
    ],
  },
  {
    title: t(i18n)`Configuración`,
    items: [
      {
        title: t(i18n)`Anuncios`,
        path: '/announcements',
        icon: Campaign,
        permission: 'Sec:Anuncios',
      },
      {
        title: t(i18n)`API Capta`,
        path: '/api',
        paramsPath: ['/api/keys'],
        icon: KeyIcon,
        permission: 'Sec:API',
      },
      {
        title: t(i18n)`Auditoría`,
        path: '/audit',
        icon: Biotech,
        permission: 'Sec:Auditoría',
      },
      {
        title: t(i18n)`Colecciones`,
        path: '/collections',
        icon: DeviceHubIcon,
        permission: 'Sec:Colecciones',
        requiresCaptaUser: true,
      },
      {
        title: t(i18n)`Configuración de Equipos`,
        path: '/device-configuration',
        icon: FolderIcon,
        permission: 'Sec:ConfiguracionEquipos',
      },
      {
        title: t(i18n)`Diseño Amaru`,
        path: '/standard-design',
        icon: DesignServicesOutlined,
        permission: 'Sec:StandardDesign',
      },
      {
        title: t(i18n)`Envío de Comandos`,
        path: '/device-commands',
        icon: ScheduleSendIcon,
        permission: 'Sec:EnvioComandos',
      },
      {
        title: t(i18n)`Facturación`,
        path: '/billing',
        icon: Payment,
        permission: 'Sec:Billing',
      },
      {
        title: t(i18n)`Notificaciones`,
        path: '/notifications',
        icon: Notifications,
        permission: 'Sec:Notificaciones',
      },
      {
        title: t(i18n)`Organización`,
        path: '/organization',
        icon: BusinessIcon,
        permission: 'Sec:Organizacion',
      },
      {
        title: t(i18n)`Permisos y Accesos`,
        path: '/access',
        icon: WifiProtectedSetupOutlinedIcon,
        permission: 'Sec:PermisoAccesos',
      },
      {
        title: t(i18n)`Sistemas`,
        path: '/systems',
        paramsPath: '/system/administrator/:systemId',
        icon: DeviceHubIcon,
        permission: 'Sec:Sistemas',
      },
      {
        title: t(i18n)`Usuarios`,
        path: '/users',
        icon: GroupIcon,
        permission: 'Sec:Usuarios',
      },
    ],
  },
];

export const useSidebarItemGroups = () => {
  const { i18n } = useLingui();
  const { hasAccess, isCaptaUser } = useAccess();
  const { isMatching } = useRouteMatch();

  const itemGroups = useMemo(() => {
    const items = ALL_SIDEBAR_ITEM_GROUPS(i18n).map((group) => {
      const items = group.items
        // filter access
        .filter((item) => {
          const hasPermission = hasAccess(item.permission);
          if (item.requiresCaptaUser) {
            return hasPermission && isCaptaUser;
          }
          return hasPermission;
        })
        // map active status
        .map((item) => {
          const pathsToCheck = [item.path];
          if (item.paramsPath) {
            if (Array.isArray(item.paramsPath)) {
              pathsToCheck.push(...item.paramsPath);
            } else {
              pathsToCheck.push(item.paramsPath);
            }
          }

          return {
            ...item,
            active: isMatching(pathsToCheck),
          };
        });
      return {
        ...group,
        items,
      };
    });

    return items.filter((group) => group.items.length > 0);
  }, [i18n, hasAccess, isCaptaUser, isMatching]);

  return itemGroups;
};
